import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

import template from "./GuestAccountTemplate.html";
import { _lcs } from "../../i18n";
import { gridLanguage } from "../../core/grid";
import { GuestFormat } from "./GuestFormat";

/**
 * GuestAccount — "Gästekonten": every guest account of the platform (role
 * "guest"), with the houses it has signed in at.
 *
 * The client-side counterpart of ClientUser ("Gastgeber"): a searchable,
 * sortable GridJS table from the admin-only GET /admin/guest. A click on a row
 * opens the account's page (GuestAccountDetail); the trash button on a row
 * deletes the account after a confirmation (see onDelete).
 */
export class GuestAccount extends InAppState
{
    static ROUTE = '/guest/users';
    static ID = 'guest-users';

    // Preview key (core-api PurgeService.previewGuest) → German source string
    // with a {count} slot (dictionary key). Only what exists is listed.
    static PURGE_KINDS = [
        [ "accommodations", "{count} Unterkunfts-Verknüpfungen" ],
        [ "plans", "{count} Reisepläne" ],
        [ "conversations", "{count} Chat-Verläufe" ],
        [ "pushSubscriptions", "{count} Push-Abonnements" ],
        [ "surveyInvitations", "{count} Umfrage-Einladungen" ]
    ];

    // The roles an account keeps, worded for the dialog.
    static ROLE_LABELS = { admin: "Admin", client: "Gastgeber" };

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="guests-state"]' );
        this.summaryEl = root.querySelector( '[data-id="guests-summary"]' );
        this.guests = [];

        // Delegated so it survives GridJS re-renders (sort/search/page).
        this.addEventListener( this.stateEl, "click", ( e ) => this.onRowClick( e ) );
        this.addEventListener( this.stateEl, "keydown", ( e ) =>
        {
            if ( e.key === "Enter" ) this.onRowClick( e );
        } );

        await this.loadGuests();
    }

    async loadGuests()
    {
        this.showMessage( "user-round", _lcs( "Lade Gästekonten …" ), _lcs( "Einen Moment bitte." ) );

        try
        {
            const { result } = await this.app.api.get( "/admin/guest" );
            if ( !result || +result.status !== 200 || !Array.isArray( result.data?.guests ) )
            {
                throw new Error( "Unexpected /admin/guest response." );
            }
            this.guests = result.data.guests;
        }
        catch ( e )
        {
            console.error( e );
            if ( this.isActive() )
            {
                this.showMessage( "triangle-alert", _lcs( "Gästekonten konnten nicht geladen werden" ),
                    _lcs( "Bitte versuche es später erneut." ) );
            }
            return;
        }

        if ( !this.isActive() )
        {
            return;
        }

        this.renderSummary();
        // An empty list is handled by GridJS itself (noRecordsFound message).
        this.renderGrid();
    }

    renderSummary()
    {
        const confirmed = this.guests.filter( ( g ) => g.isValidated ).length;
        const atAHouse = this.guests.filter( ( g ) => g.accommodations.length > 0 ).length;
        const writing = this.guests.filter( ( g ) => g.messageCount > 0 ).length;

        const tiles = [
            { icon : "user-round", label : _lcs( "Gästekonten" ), value : this.guests.length },
            { icon : "shield-check", label : _lcs( "E-Mail bestätigt" ), value : confirmed },
            { icon : "building-2", label : _lcs( "In einer Unterkunft angemeldet" ), value : atAHouse },
            { icon : "message-circle", label : _lcs( "Haben geschrieben" ), value : writing }
        ];

        this.summaryEl.innerHTML = tiles.map( ( t ) =>
            '<div class="guests__stat">' +
                '<span class="guests__stat-icon"><i class="icon icon--' + t.icon + '"></i></span>' +
                '<span class="guests__stat-text">' +
                    '<span class="guests__stat-value">' + GuestFormat.esc( t.value ) + '</span>' +
                    '<span class="guests__stat-label">' + GuestFormat.esc( t.label ) + '</span>' +
                '</span>' +
            '</div>' ).join( "" );
        this.summaryEl.hidden = false;
    }

    renderGrid()
    {
        this._destroyGrid();
        this.stateEl.innerHTML = "";

        // Sortable cells hold sortable values; formatters make them readable.
        // The id rides as the (hidden) last cell.
        const rows = this.guests.map( ( g ) => [
            g.name,
            g.email,
            g.status,
            g.accommodations.map( ( a ) => a.name ).join( ", " ),
            g.messageCount,
            g.planCount,
            g.lastSeenAt || "",
            g.registeredAt || "",
            "",                 // actions — rendered by the formatter
            g.id
        ] );

        const byId = new Map( this.guests.map( ( g ) => [ g.id, g ] ) );
        const guestOf = ( row ) => byId.get( row.cells[ row.cells.length - 1 ].data );

        this.grid = new Grid( {
            columns: [
                {
                    name: _lcs( "Gast" ),
                    formatter: ( cell, row ) =>
                    {
                        const g = guestOf( row );
                        return html(
                            '<span class="guests__who" data-guest-id="' + GuestFormat.esc( g.id ) + '" tabindex="0" role="link">' +
                                '<span class="guests__avatar">' + GuestFormat.esc( GuestFormat.initials( g.name ) ) + '</span>' +
                                '<span class="guests__name">' + GuestFormat.esc( cell ) + '</span>' +
                            '</span>' );
                    }
                },
                _lcs( "E-Mail" ),
                {
                    name: _lcs( "Status" ),
                    formatter: ( cell ) => html( GuestFormat.statusPill( cell ) )
                },
                {
                    name: _lcs( "Unterkünfte" ),
                    formatter: ( cell ) => html( cell
                        ? '<span class="guests__houses" title="' + GuestFormat.esc( cell ) + '">' + GuestFormat.esc( cell ) + '</span>'
                        : '<span class="guests__none">—</span>' )
                },
                _lcs( "Nachrichten" ),
                _lcs( "Pläne" ),
                {
                    name: _lcs( "Zuletzt aktiv" ),
                    formatter: ( cell ) => GuestFormat.date( cell ) || "—"
                },
                {
                    name: _lcs( "Registriert" ),
                    formatter: ( cell ) => GuestFormat.date( cell ) || "—"
                },
                {
                    name: _lcs( "Aktionen" ),
                    sort: false,
                    formatter: ( cell, row ) => html( this._deleteButtonHtml( guestOf( row ).id ) )
                },
                { name: "id", hidden: true }
            ],
            data: rows,
            // Let the table size its columns to their content, so the
            // headers are not cut off at a fixed share of the width.
            autoWidth: false,
            search: true,
            sort: true,
            pagination: { limit: 25, summary: true },
            className: { container: "admin-users__grid guests__grid" },
            language: gridLanguage( _lcs( "Gästekonten" ), _lcs( "Keine Gästekonten gefunden" ) )
        } );

        this.grid.render( this.stateEl );
    }

    /** The whole row is the link — the name cell carries the id. */
    onRowClick( e )
    {
        // The delete button sits inside the row; it must not open the page.
        const del = e.target.closest( '[data-action="delete"]' );
        if ( del && this.stateEl.contains( del ) )
        {
            e.preventDefault();
            const guest = this.guests.find( ( g ) => g.id === del.getAttribute( "data-guest-id" ) );
            if ( guest && e.type === "click" )
            {
                this.onDelete( guest );
            }
            return;
        }

        const row = e.target.closest( "tr.gridjs-tr" );
        const who = row ? row.querySelector( "[data-guest-id]" ) : null;
        if ( !who )
        {
            return;
        }
        this.app.router.redirect( "/guest/users/" + encodeURIComponent( who.getAttribute( "data-guest-id" ) ) );
    }

    /* ------------------------------- Delete --------------------------------- */

    /**
     * Delete a guest account (DELETE /admin/guest/:id), only after the
     * confirmation. What goes is listed from GET /admin/guest/:id/purge: an
     * account that is nothing but a guest is deleted for good; one with
     * further roles (a host, an operator) keeps the account and loses only the
     * guest role with its guest data. Nobody is mailed.
     */
    async onDelete( guest )
    {
        const name = guest.name || guest.email || _lcs( "dieses Gastkonto" );

        // Without the preview there is no dialog — and no deletion.
        let preview;
        try
        {
            const { result } = await this.app.api.get( "/admin/guest/" + encodeURIComponent( guest.id ) + "/purge" );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected guest purge preview." );
            }
            preview = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( this._deleteErrorForStatus( +e?.status ), "error" );
            return;
        }

        const lines = GuestAccount.PURGE_KINDS
            .filter( ( [ key ] ) => +preview[ key ] > 0 )
            .map( ( [ key, label ] ) => "• " + _lcs( label, { count : +preview[ key ] } ) );
        const details = lines.length ? "\n\n" + _lcs( "Dabei werden gelöscht:" ) + "\n" + lines.join( "\n" ) : "";

        const fullDelete = preview.deletesAccount === true;
        const roles = ( preview.otherRoles || [] ).map( ( r ) => GuestAccount.ROLE_LABELS[ r ] ? _lcs( GuestAccount.ROLE_LABELS[ r ] ) : r ).join( ", " );

        const ok = await this.app.view.confirm(
            fullDelete ? _lcs( "Gastkonto endgültig löschen?" ) : _lcs( "Gast-Rolle entfernen?" ),
            ( fullDelete
                ? _lcs( "„{name}“ hat nur die Gast-Rolle. Das Konto wird mit allen zugehörigen Daten unwiderruflich gelöscht und kann nicht wiederhergestellt werden.", { name } )
                : _lcs( "„{name}“ hat weitere Rollen ({roles}). Es wird nur die Gast-Rolle mit den Gast-Daten entfernt; das Konto bleibt bestehen. Alle Sitzungen des Kontos werden beendet.", { name, roles } )
            ) + details,
            fullDelete ? _lcs( "Endgültig löschen" ) : _lcs( "Rolle entfernen" ),
            _lcs( "Abbrechen" ),
            { danger: true }
        );
        if ( !ok )
        {
            return;
        }

        try
        {
            const { result } = await this.app.api.delete( "/admin/guest/" + encodeURIComponent( guest.id ) );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected delete response." );
            }
            this.app.view.toast(
                result.data && result.data.deleted ? _lcs( "Gastkonto wurde gelöscht." ) : _lcs( "Gast-Rolle wurde entfernt." ),
                "success"
            );
            await this.loadGuests();
        }
        catch ( err )
        {
            this.app.view.toast( this._deleteErrorForStatus( +err?.status ), "error" );
        }
    }

    _deleteButtonHtml( id )
    {
        const safe = GuestFormat.esc( id );
        const del = GuestFormat.esc( _lcs( "Löschen" ) );
        return '<div class="admin-users__actions">' +
            '<button type="button" class="admin-users__action admin-users__action--danger" data-action="delete" data-guest-id="' + safe + '" title="' + del + '" aria-label="' + del + '">' +
                '<i class="icon icon--trash-2"></i><span>' + del + '</span>' +
            '</button>' +
        '</div>';
    }

    _deleteErrorForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Aktion nicht möglich (ggf. eigenes Konto)." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Gastkonto wurde nicht gefunden." );
            default:  return _lcs( "Löschen fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    showMessage( icon, title, text )
    {
        this._destroyGrid();
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
            '</div>';
        // textContent assignment avoids HTML injection from dynamic strings.
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    _destroyGrid()
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* DOM is replaced anyway */ }
            this.grid = null;
        }
    }

    async exit()
    {
        this._destroyGrid();
        // Base InAppState clears the page-content container.
        await super.exit();
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Gästekonten" ),
            subtitle: _lcs( "Registrierte Gäste der Plattform" )
        };
    }
}
